import type { AsyncSequence } from "#public"

/**
 * Filters async sequences based on a predicate function.
 * Only items that satisfy the predicate are yielded.
 *
 * @param func - Predicate function that returns true to keep the item, false to filter it out
 * @param arrays - One or more async sequences to filter
 * @returns An async generator yielding only items that pass the predicate
 *
 * @example
 * ```ts
 * import { FilterAsync } from "@pdfking/tfunc"
 *
 * async function* numbers() {
 *   yield 1;
 *   yield 2;
 *   yield 3;
 *   yield 4;
 * }
 *
 * const evenNumbers = FilterAsync(n => n % 2 === 0, numbers());
 * for await (const num of evenNumbers) {
 *   console.log(num); // 2, 4
 * }
 * ```
 */
export const FilterAsync = async function* <T>(
  func: (item: T, index: number) => boolean | Promise<boolean>,
  ...arrays: AsyncSequence<T>[]
): AsyncGenerator<T> {
  let index = 0
  for (const array of arrays) {
    for await (const item of array) {
      if (await func(item, index++)) {
        yield item
      }
    }
  }
}
